'use client';

import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useCart } from '@/contexts/CartContext';
import { useThrottle } from '@/lib/hooks/useThrottle';
import { logCartEvent } from '@/lib/cart/logCartEvent';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export function EditItemQuantityButton({ item, type, position }) {
  const { cart, addCartItems, deleteCartItem, isCartLoading, cartLoadingAction } = useCart();

  const currentItem = cart?.lines?.find((line) => line.id === item.id);
  const resolvedVariant = currentItem?.merchandise?.product?.variants?.find(
    (variant) => variant.id === currentItem.merchandise.id,
  );
  // Shopify returned this line's product with no variant edges (deleted
  // product, sync error, or partial GraphQL response) — the '+' action has
  // nothing to add, so disable it instead of sending variant: undefined.
  const variantUnavailable = type === 'plus' && !!currentItem && !resolvedVariant;

  const handleClick = useThrottle(
    async () => {
      // Prevent double-firing while any cart action is still in progress.
      if (isCartLoading) return;

      const currentItem = cart?.lines?.find((line) => line.id === item.id);
      if (!currentItem || !currentItem?.id) return;

      // Re-resolve item from cart state to avoid stale closure data.
      if (type === 'plus') {
        const resolvedVariant = currentItem.merchandise.product.variants.find(
          (variant) => variant.id === currentItem.merchandise.id,
        );

        if (!resolvedVariant) {
          logCartEvent(
            'CART_QUANTITY_VARIANT_UNRESOLVED',
            {
              lineId: currentItem.id,
              productId: currentItem.merchandise.product?.id,
            },
            'warn',
          );

          return;
        }

        await addCartItems(
          [
            {
              variant: resolvedVariant,
              product: currentItem.merchandise.product,
              quantity: 1,
              customAttributes: [
                {
                  key: '_productType',
                  value: currentItem.merchandise.product.productType,
                },
                ...(currentItem.attributes ?? []).filter(
                  (attribute) => attribute.key === '_productUrl',
                ),
                { key: '_split_id', value: crypto.randomUUID() },
              ],
            },
          ],
          'cart_quantity_controls', // source, used for tracking
          'plus',                   // loadingAction: drives the global cart spinner
        );
      } else if (type === 'minus') {
        // Only delete when quantity would reach zero; decrement (qty > 1) is not
        // yet implemented — each split-line added via '+' always has quantity 1.
        if ((currentItem.quantity ?? 1) <= 1) {
          await deleteCartItem([currentItem.id], 'cart_quantity_controls');
        }
      }
    },
    200,
    { leading: true, trailing: false },
  );

  const show_quantity_buttons =
    item.attributes?.find((attribute) => attribute.key.includes('promoType'))
      ?.value !== 'free';

  return (
    <>
      {show_quantity_buttons ? (
        <button
          onClick={handleClick}
          aria-label={
            variantUnavailable
              ? 'This item is unavailable and can’t be updated'
              : type === 'plus'
                ? 'Increase item quantity'
                : 'Reduce item quantity'
          }
          title={variantUnavailable ? 'This item is unavailable and can’t be updated' : undefined}
          className={`h-5 w-5 justify-self-center text-primary ${
            variantUnavailable ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
          } ${type === 'minus' ? 'ml-auto' : ''}`}
          disabled={isCartLoading || variantUnavailable}
        >
          {/* Show a spinner matching the active action; fall back to the static icon. */}
          {cartLoadingAction === type ? (
            <SpinnerIcon className="h-4 w-4 shrink-0 animate-spin" />
          ) : type === 'minus' ? (
            <TrashIcon position={position} className="h-4 w-4" />
          ) : (
            <PlusIcon position={position} className="h-4 w-4" />
          )}
        </button>
      ) : (
        ''
      )}
    </>
  );
}
